import { Box, Tabs, Tab, Button } from "@mui/material";
import { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import React from "react";
import StoreOrders from "./storeOrders";
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Bar, Tooltip } from "recharts";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useGetStoreDetailQuery } from "../../store/api/manageAPI";
import Loader from "../../constants/Loader";
import { showError } from "../../constants/toasts";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

type DayOfWeek =
  | "Monday"
  | "Tuesday"
  | "Wednesday"
  | "Thursday"
  | "Friday"
  | "Saturday"
  | "Sunday";

interface Availability {
  enabled: boolean;
  start: string;
  end: string;
}

const dauData = [
  { name: "Mon", users: 120 },
  { name: "Tue", users: 210 },
  { name: "Wed", users: 180 },
  { name: "Thu", users: 300 },
  { name: "Fri", users: 260 },
  { name: "Sat", users: 140 },
  { name: "Sun", users: 170 },
];

const StoreDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const storeId = (location.state as any)?.storeId;

  const [valueTabs, setValueTabs] = useState(0);
  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  // Fetch store details from API
  const { data, isLoading, error } = useGetStoreDetailQuery(
    { id: storeId },
    { skip: !storeId }
  );

  const storeData = data?.data?.data?.[0];
  const storeCategories = data?.data?.storeCategory || [];
  const storeProducts = data?.data?.storeProduct || [];
  const storeFlavors = data?.data?.storeFlavor || [];
  const storeToppings = data?.data?.storeTopping || [];

  // Extract availability from API response - must be called before any conditional returns
  const availability = useMemo(() => {
    if (!storeData?.storeAvailability) {
      return {
        Monday: { enabled: false, start: "09:00", end: "18:00" },
        Tuesday: { enabled: false, start: "09:00", end: "18:00" },
        Wednesday: { enabled: false, start: "09:00", end: "18:00" },
        Thursday: { enabled: false, start: "09:00", end: "18:00" },
        Friday: { enabled: false, start: "09:00", end: "18:00" },
        Saturday: { enabled: false, start: "09:00", end: "18:00" },
        Sunday: { enabled: false, start: "09:00", end: "18:00" },
      };
    }

    const weekdayMap: Record<string, DayOfWeek> = {
      weekday1: "Monday",
      weekday2: "Tuesday",
      weekday3: "Wednesday",
      weekday4: "Thursday",
      weekday5: "Friday",
      weekday6: "Saturday",
      weekday7: "Sunday",
    };

    const result: Record<DayOfWeek, Availability> = {
      Monday: { enabled: false, start: "09:00", end: "18:00" },
      Tuesday: { enabled: false, start: "09:00", end: "18:00" },
      Wednesday: { enabled: false, start: "09:00", end: "18:00" },
      Thursday: { enabled: false, start: "09:00", end: "18:00" },
      Friday: { enabled: false, start: "09:00", end: "18:00" },
      Saturday: { enabled: false, start: "09:00", end: "18:00" },
      Sunday: { enabled: false, start: "09:00", end: "18:00" },
    };

    Object.keys(weekdayMap).forEach((key) => {
      const day = weekdayMap[key];
      const dayData = storeData.storeAvailability?.[key as keyof typeof storeData.storeAvailability];
      if (dayData?.isActive) {
        const startTime = dayData.startTime
          ? new Date(dayData.startTime).toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            })
          : "09:00";
        const endTime = dayData.endTime
          ? new Date(dayData.endTime).toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            })
          : "18:00";
        result[day] = { enabled: true, start: startTime, end: endTime };
      }
    });

    return result;
  }, [storeData]);

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Calculate current status based on today's availability
  const currentStatus = useMemo(() => {
    if (!storeData?.storeAvailability) return "Closed";
    
    // Get current day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    const currentDay = new Date().getDay();
    
    // Find the weekday object that matches the current day
    let isTodayActive = false;
    const weekdayKeys = Object.keys(storeData.storeAvailability);
    for (const key of weekdayKeys) {
      const weekdayData = storeData.storeAvailability[key as keyof typeof storeData.storeAvailability];
      if (weekdayData && weekdayData.day === currentDay) {
        isTodayActive = weekdayData.isActive === true;
        break;
      }
    }
    
    return isTodayActive ? "Open" : "Closed";
  }, [storeData]);

  // Show error if store ID is missing or API call fails
  if (!storeId) {
    showError("Store ID is missing");
    navigate("/manage-stores");
  }

  if (error) {
    showError("Failed to fetch store details");
  }

  return (
    <>
      <Loader isLoad={isLoading} />
      <div className="main_title">
        <h1>#{storeData?.code || "N/A"} - Store Detail</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> - {" "}
          <button onClick={() => navigate("/manage-stores")}>Store Management</button> -
          Store Detail
        </p>
      </div>

      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label="General Store Info" {...a11yProps(0)} />
              <Tab label="Store Availability" {...a11yProps(1)} />
              <Tab label="Orders" {...a11yProps(2)} />
              <Tab label="Revenue" {...a11yProps(2)} />
            </Tabs>
          </Box>
        </div>
      </div>

      <div className="cards view_page">
        <CustomTabPanel value={valueTabs} index={0}>
          <div className="gap_p">
            <div className="vew_img v2 no_radius w_100">
              <h6>Store Image</h6>
              <figure>
                <img
                  alt="store"
                  src={
                    storeData?.image?.startsWith("http")
                      ? storeData.image
                      : storeData?.image
                      ? `/static/images/${storeData.image}`
                      : "/static/images/placeholder.png"
                  }
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "/static/images/placeholder.png";
                  }}
                />
              </figure>
            </div>

            <div className="w_33">
              <h6>Store Code</h6>
              <p>#{storeData?.code || "N/A"}</p>
            </div>

            <div className="w_33">
              <h6>Store Name</h6>
              <p>{storeData?.name || "N/A"}</p>
            </div>

            <div className="w_33">
              <h6>Status</h6>
              <p>
                <span
                  className={`status_btn dark ${currentStatus === "Open"
                    ? "c_success"
                    : currentStatus === "Closed"
                      ? "c_danger"
                      : ""
                    }`}
                >
                  {currentStatus}
                </span>
              </p>
            </div>

            {storeData?.address?.city && (
              <div className="w_33">
                <h6>City</h6>
                <p>{storeData.address.city}</p>
              </div>
            )}

            {storeData?.address?.fullAddress && (
              <div className="w_100">
                <h6>Full Address</h6>
                <p>{storeData.address.fullAddress}</p>
              </div>
            )}

            {storeData?.address?.street && (
              <div className="w_33">
                <h6>Street</h6>
                <p>{storeData.address.street}</p>
              </div>
            )}

            {storeData?.address?.state && (
              <div className="w_33">
                <h6>State</h6>
                <p>{storeData.address.state}</p>
              </div>
            )}

            {storeData?.address?.country && (
              <div className="w_33">
                <h6>Country</h6>
                <p>{storeData.address.country}</p>
              </div>
            )}

            {storeData?.address?.zipCode && (
              <div className="w_33">
                <h6>Zip Code</h6>
                <p>{storeData.address.zipCode}</p>
              </div>
            )}

            {storeData?.region && (
              <div className="w_33">
                <h6>Region</h6>
                <p>{storeData.region}</p>
              </div>
            )}

            <div className="w_33">
              <h6>Created Date</h6>
              <p>{formatDate(storeData?.createdAt)}</p>
            </div>

            {storeFlavors.length > 0 && (
              <div className="w_100">
                <h6>Flavours</h6>
                <p>
                  {storeFlavors
                    .map((flavor: any) => flavor.flavorDetails?.name)
                    .filter(Boolean)
                    .join(", ") || "N/A"}
                </p>
              </div>
            )}

            {storeToppings.length > 0 && (
              <div className="w_100">
                <h6>Toppings</h6>
                <p>
                  {storeToppings
                    .map((topping: any) => topping.toppingDetails?.name)
                    .filter(Boolean)
                    .join(", ") || "N/A"}
                </p>
              </div>
            )}

            {storeCategories.length > 0 && (
              <div className="w_100">
                <h6>Categories</h6>
                <p>
                  {storeCategories
                    .map((category: any) => category.categoryDetails?.name)
                    .filter(Boolean)
                    .join(", ") || "N/A"}
                </p>
              </div>
            )}

            {storeProducts.length > 0 && (
              <div className="w_100">
                <h6>Products</h6>
                <p>
                  {storeProducts
                    .map((product: any) => product.productDetails?.name)
                    .filter(Boolean)
                    .join(", ") || "N/A"}
                </p>
              </div>
            )}
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={1}>
          <div className="gap_p">
            <div className="w_100">
              <h6>Weekly Availability</h6>
              <ul className="availability_list">
                {(Object.keys(availability) as DayOfWeek[]).map((day) =>
                  availability[day].enabled ? (
                    <li key={day}>
                      <strong>{day}:</strong> {availability[day].start} - {availability[day].end}
                    </li>
                  ) : null
                )}
                {!Object.values(availability).some((a) => a.enabled) && (
                  <li>No availability set</li>
                )}
              </ul>
            </div>
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={2}>
          <StoreOrders />
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={3}>


          <div className="stats_boxes gap_m">
            <div className="cards">
              <figure>
                <img src="/static/images/order.svg" alt="Revenue" />
              </figure>
              <h2>200</h2>
              <p>Total Order</p>
            </div>
            <div className="cards">
              <figure>
                <img src="/static/images/user_icon.svg" alt="Users" />
              </figure>
              <h2>$800</h2>
              <p>Today Earning</p>
            </div>
            <div className="cards">
              <figure>
                <img src="/static/images/user_icon.svg" alt="Active Users" />
              </figure>
              <h2>$5000</h2>
              <p>Total Earning</p>
            </div>
          </div>

          <div className="grid_sc gap_m">
            <div className="cards table_card">
              <div className="cards_header">
                <div className="left_s">
                  <h2>Total Revenue</h2>
                </div>
                <div className="right_s">
                  <p>
                    <span>A day</span>
                    <span>A week</span>
                    <span>A month</span>
                    <span>A year</span>
                  </p>
                  <Button className="btn btn_black icon_btn"><FileDownloadIcon /> </Button>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dauData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="users" fill="#38B6FF" />
                </BarChart>
              </ResponsiveContainer>
            </div>

          </div>
        </CustomTabPanel>
      </div>
    </>
  );
};

export default StoreDetail;
